import {
  ApplicationRef,
  ComponentRef,
  ElementRef,
  Injectable,
  Injector,
  ViewContainerRef,
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TourBubbleComponent } from '@generic/tour-bubble/tour-bubble.component';
/**
 * TourStep interface
 * @param stepId - The step id
 * @param title - The step title
 * @param content - The step content
 * @param element - The step element
 * @param position - The step position
 * @param end - Whether the step is the last step
 * @param fastForward - Whether the step will be automatically skipped if the next step element is present
 * @param canSkip - Whether the step can be skipped
 * @param skipIf - Optional function that returns true if step should be skipped (condition already met)
 */
export interface TourStep {
  stepId?: string;
  title?: string;
  content?: string;
  element?: any;
  position?: string;
  end?: boolean;
  fastForward?: boolean;
  canSkip?: boolean;
  skipIf?: () => boolean;
}

/**
 * TourConfig interface for tour-level settings
 */
export interface TourConfig {
  steps: TourStep[];
  /** Optional setup function that runs before the tour starts (e.g., to auto-expand menus) */
  setup?: () => void | Promise<void>;
  /** Delay in ms after setup before starting the tour (default: 500) */
  setupDelay?: number;
}

/**
 * TourService
 * @description This service is used to manage the tours.
 * This service is used along with the tourTarget directive to render the tour bubble attached to the target element with these attributes:
 *  tourId{string} - The tour id
 *  stepId{string} - The step id
 *  tourPosition{string} - The position of the bubble relative to the target element - will override the position of the step in provided in defineTour method
 * *  See the directive for more specific guidelines
 *
 * To create a tour, you need to define the steps and the tour id and pass them into the defineTour method.
 *
 * To start a tour, you need to call the startTour method with the tour id.
 *
 * To end a tour, you need to call the endTour method with the tour id.
 *
 * To advance to the next step, you need to call the nextStep method with the tour id and the current step id.
 * This can be triggered by any user interaction that has a definable event
 * ie in the click event of a component: this.tourService.nextStep('my-tour', 'click-button-step');
 *
 * To remove a tour element, you need to call the removeTourElement method with the tour id and the step id.
 *
 */

@Injectable({
  providedIn: 'root',
})
export class TourService {
  private activeTour: string;
  private tours: Map<string, TourStep[]> = new Map<string, TourStep[]>();
  private tourConfigs: Map<string, TourConfig> = new Map<string, TourConfig>();
  private currentStep: BehaviorSubject<TourStep | null> =
    new BehaviorSubject<TourStep | null>(null);
  private storedSteps: any = {};
  private componentRef: ComponentRef<TourBubbleComponent>;
  private viewContainerRef: ViewContainerRef;
  private bubbleHistory = [];
  private visibilityObserver: IntersectionObserver | null = null;
  constructor(
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  /**
   * Defines a tour with the given steps.
   * @param tourId - The tour id
   * @param stepsOrConfig - The steps of the tour, or a TourConfig object
   */
  public defineTour(
    tourId: string,
    stepsOrConfig: TourStep[] | TourConfig
  ): void {
    const config: TourConfig = Array.isArray(stepsOrConfig)
      ? { steps: stepsOrConfig }
      : stepsOrConfig;

    const steps = config.steps;

    if (this.tours.has(tourId)) {
      let existingSteps = this.tours.get(tourId);
      steps.forEach(step => {
        if (
          !existingSteps.some(
            existingStep => existingStep.stepId === step.stepId
          )
        ) {
          existingSteps.push(step);
        }
      });
      this.tours.set(tourId, existingSteps);
    } else {
      this.tours.set(tourId, steps);
    }

    this.tourConfigs.set(tourId, config);
  }

  /**
   * Starts the tour with the given tour id.
   * Runs the setup function first if defined.
   * @param tourId - The tour id
   * @param startFromStep - Optional step ID to start from (skips setup steps)
   */
  public async startTour(
    tourId: string,
    startFromStep?: string
  ): Promise<void> {
    this.currentStep.next(null);
    this.activeTour = tourId;

    const config = this.tourConfigs.get(tourId);

    if (config?.setup) {
      try {
        console.log(`Running setup for tour: ${tourId}`);
        await config.setup();
        const delay = config.setupDelay ?? 500;
        await new Promise(resolve => setTimeout(resolve, delay));
      } catch (e) {
        console.warn(`Error running setup for tour ${tourId}:`, e);
      }
    }

    if (startFromStep) {
      const steps = this.tours.get(tourId);
      const stepIndex = steps?.findIndex(s => s.stepId === startFromStep);
      if (stepIndex !== undefined && stepIndex >= 0) {
        const prevStep = stepIndex > 0 ? steps[stepIndex - 1] : null;
        this.currentStep.next(prevStep);
      }
    }

    this.nextStep(tourId);
  }

  /**
   * Ends the tour with the given tour id.
   * @param tourId - The tour id
   */
  endTour(tourId: string) {
    this.activeTour = null;
    this.currentStep.next(null);
    this.cleanupVisibilityObserver();
    this.destroyTourBubble();
  }

  /**
   * Advances to the next step of the tour with the given tour id.
   * @param tourId - The tour id
   * @param currentStepId - The current step id
   */
  public nextStep(tourId: string, currentStepId?: string): void {
    if (!tourId) {
      return;
    }
    //support for csv for tours and steps
    let tours = tourId.split(',');
    let currentSteps = currentStepId?.split(',') || [];
    let active = false;

    tours.forEach((tour, index) => {
      if (this.activeTour === tour) {
        active = true;
      }
    });
    if (!active) {
      return;
    }
    tourId = this.activeTour;
    currentStepId = currentSteps[tours.indexOf(tourId)];
    const steps = this.tours.get(tourId);
    const currentStep = this.currentStep.value;
    if (
      steps &&
      (currentStepId === undefined || currentStep?.stepId === currentStepId)
    ) {
      const currentIndex = currentStep
        ? steps.findIndex(step => step.stepId === currentStep.stepId)
        : -1;

      let nextStep = this.findNextValidStep(steps, currentIndex);

      if (nextStep?.end) {
        this.currentStep.next(null);
        this.cleanupVisibilityObserver();
        this.destroyTourBubble();
      } else if (nextStep) {
        this.currentStep.next(nextStep);
        if (this.checkFastForward(tourId)) {
          return this.nextStep(tourId);
        }
        this.destroyTourBubble();
        this.cleanupVisibilityObserver();
        console.log('NEXT STEP', nextStep);
        if (nextStep.element && this.isElementVisible(nextStep.element)) {
          this.renderCurrentStep(tourId);
        } else if (nextStep.element) {
          this.observeElementVisibility(tourId, nextStep.element);
        }
      } else {
        this.destroyTourBubble();
        //await a new step
      }
    }
  }

  private findNextValidStep(
    steps: TourStep[],
    currentIndex: number
  ): TourStep | undefined {
    for (let i = currentIndex + 1; i < steps.length; i++) {
      const step = steps[i];
      if (step.skipIf && typeof step.skipIf === 'function') {
        try {
          if (step.skipIf()) {
            console.log(`Skipping step ${step.stepId} - condition already met`);
            continue; 
          }
        } catch (e) {
          console.warn(`Error evaluating skipIf for step ${step.stepId}:`, e);
        }
      }
      return step;
    }
  }

  private isElementVisible(elementRef: ElementRef): boolean {
    if (!elementRef?.nativeElement) return false;
    const el = elementRef.nativeElement;
    const rect = el.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.top < window.innerHeight &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.right > 0
    );
  }

  private observeElementVisibility(tourId: string, elementRef: ElementRef): void {
    if (!elementRef?.nativeElement) return;

    this.cleanupVisibilityObserver();

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          this.cleanupVisibilityObserver();
          if (this.currentStep.value?.element === elementRef) {
            this.renderCurrentStep(tourId);
          }
        }
      },
      {
        root: null,
        threshold: 0.1,
      }
    );

    this.visibilityObserver.observe(elementRef.nativeElement);
  }

  private cleanupVisibilityObserver(): void {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
  }
  /**
   * Gets the current step of the tour with the given tour id.
   * @param tourId - The tour id
   */
  public getCurrentStep(): Observable<TourStep | null> {
    return this.currentStep.asObservable();
  }

  /**
   * Checks if the next step of the tour with the given tour id can be skipped.
   * @param tourId - The tour id
   */
  checkFastForward(tourId: string) {
    const steps = this.tours.get(tourId);
    const currentStep = this.currentStep.value;
    const currentIndex = steps.findIndex(
      step => step.stepId === currentStep.stepId
    );
    const nextStep = steps[currentIndex + 1];
    if (nextStep?.element && currentStep.fastForward) {
      return true;
    }
    return false;
  }

  public renderCurrentStep(tourId: string): void {
    const currentStepValue = this.currentStep.value;
    this.createTourBubble(tourId, currentStepValue);
  }

  registerTourStep(
    tourId: string,
    stepId: string,
    element: ElementRef,
    position: string
  ) {
    const steps = this.tours.get(tourId);
    if (steps) {
      const step = steps.find(step => step.stepId === stepId);
      if (step) {
        step.element = element;
        if (position) {
          step.position = position;
        }
      }
    } else {
      if (!this.storedSteps[tourId]) {
        this.storedSteps[tourId] = {};
      }
      this.storedSteps[tourId][stepId] = { element, position };
    }
    if (this.currentStep.value?.stepId === stepId) {
      if (this.isElementVisible(element)) {
        this.renderCurrentStep(tourId);
      } else {
        this.observeElementVisibility(tourId, element);
      }
    }
  }

  removeTourElement(tourId: string, stepId: string) {
    if (tourId && stepId) {
      if (!this.tours.has(tourId)) {
        return;
      }
      const steps = this.tours.get(tourId);
      const step = steps.find(step => step.stepId === stepId);
      const currentStep = this.currentStep.value;
      if (currentStep?.stepId === stepId) {
        this.destroyTourBubble();
      }
      step.element = null;
    }
  }

  registerContainerRef(containerRef: ViewContainerRef) {
    this.viewContainerRef = containerRef;
  }

  private createTourBubble(tourId: string, step: TourStep): void {
    //investigat why a random unit test unrelated to this component fails when this.viewContainerRef is null
    if (!this.viewContainerRef) {
      return;
    }
    console.log('CREATE TOUR BUBBLE', step);
    this.destroyTourBubble();
    setTimeout(() => {
      this.componentRef =
        this.viewContainerRef.createComponent(TourBubbleComponent);
      this.bubbleHistory.push(this.componentRef);
      this.componentRef.instance.tourId = tourId;
      this.componentRef.instance.step = step;
      this.componentRef.changeDetectorRef.detectChanges(); // Trigger change detection
    }, 300);
  }

  private destroyTourBubble(): void {
    if (this.componentRef) {
      const componentRef = this.componentRef;
      const instance = componentRef.instance;
      instance.animationState = 'leave'; // Assuming you have an animation state control
      componentRef.changeDetectorRef.detectChanges(); // Allow animation to start

      // Delay the destruction to allow the leave animation to play
      setTimeout(() => {
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
        this.clearBubbleHistory();
      }, 300); // Adjust time based on your animation duration
    }
  }

  clearBubbleHistory() {
    this.bubbleHistory.forEach(bubble => {
      if (bubble) {
        try {
          const componentRef = bubble;
          const instance = componentRef.instance;
          instance.animationState = 'leave'; // Assuming you have an animation state control
          componentRef.changeDetectorRef.detectChanges(); // Allow animation to start

          // Delay the destruction to allow the leave animation to play
          setTimeout(() => {
            this.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
          }); // Adjust time based on your animation duration
        } catch (e) {}
      }
    });
    this.bubbleHistory = [];
  }
}
